import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, ShieldCheck, X } from "lucide-react";
import { Link } from "react-router-dom";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

const LINKS = [
  { key: "nav.tjanster", href: "#tjanster", testid: "nav-link-tjanster" },
  { key: "nav.process", href: "#process", testid: "nav-link-process" },
  { key: "nav.projekt", href: "#projekt", testid: "nav-link-projekt" },
  { key: "nav.priser", href: "#paket", testid: "nav-link-priser" },
  { key: "nav.kontakt", href: "#kontakt", testid: "nav-link-kontakt" },
];

export const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="lang-toggle" className="flex items-center rounded-full border border-white/15 p-0.5">
      {["sv", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
            lang === l ? "bg-mint text-ink" : "text-fog hover:text-bone"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const AccountLinks = ({ mobile = false }) => {
  const { user, isAdmin } = useAuth();
  const { t } = useLang();
  const base = mobile
    ? "py-3 text-left font-display text-2xl font-bold text-bone"
    : "text-sm font-medium text-fog transition-colors duration-300 hover:text-bone";
  return (
    <>
      <Link data-testid={`${mobile ? "mobile-" : ""}nav-account-link`} to={user ? "/mitt-konto" : "/logga-in"} className={base}>
        {user ? t("nav.account") : t("nav.login")}
      </Link>
      {isAdmin && (
        <Link
          data-testid={`${mobile ? "mobile-" : ""}nav-admin-link`}
          to="/admin"
          className={
            mobile
              ? "flex items-center gap-2 py-3 font-display text-2xl font-bold text-mint"
              : "flex items-center gap-1.5 rounded-full border border-mint/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mint transition-colors duration-300 hover:bg-mint hover:text-ink"
          }
        >
          <ShieldCheck className={mobile ? "h-5 w-5" : "h-3 w-3"} /> Admin
        </Link>
      )}
    </>
  );
};

export const Logo = ({ onClick }) => (
  <button
    data-testid="nav-logo"
    onClick={onClick}
    className="flex items-baseline gap-1 font-display text-xl font-extrabold tracking-tight text-bone"
    aria-label="Nivå Digital – till toppen"
  >
    NIVÅ<span className="inline-block h-2 w-2 rounded-full bg-mint" />
  </button>
);

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "border-b border-white/10 bg-ink/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <Logo onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} />
        <div className="hidden items-center gap-8 md:flex">
          {LINKS.map((link) => (
            <button
              key={link.href}
              data-testid={link.testid}
              onClick={() => go(link.href)}
              className="text-sm font-medium text-fog transition-colors duration-300 hover:text-bone"
            >
              {t(link.key)}
            </button>
          ))}
          <AccountLinks />
          <LangToggle />
          <button
            data-testid="nav-cta-button"
            onClick={() => go("#boka")}
            className="group flex items-center gap-2 rounded-full bg-bone px-5 py-2.5 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-mint"
          >
            {t("nav.boka")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
        <div className="flex items-center gap-3 md:hidden">
          <LangToggle />
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="text-bone"
            aria-label={open ? "Stäng meny" : "Öppna meny"}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-b border-white/10 bg-ink/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 pb-8 pt-2">
              {LINKS.map((link) => (
                <button
                  key={link.href}
                  data-testid={`mobile-${link.testid}`}
                  onClick={() => go(link.href)}
                  className="py-3 text-left font-display text-2xl font-bold text-bone"
                >
                  {t(link.key)}
                </button>
              ))}
              <AccountLinks mobile />
              <button
                data-testid="mobile-nav-cta-button"
                onClick={() => go("#boka")}
                className="mt-4 flex items-center justify-center gap-2 rounded-full bg-mint px-5 py-3.5 text-sm font-bold text-ink"
              >
                {t("nav.bokaLong")}
                <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
